import { CAPTURE_SESSION_STATUSES } from '@/shared/capture/capture-session';
import { type FieldDatabase, SYNC_QUEUE_STATUSES } from '@/shared/db/schema';

export interface StorageHealth {
  draftCount: number;
  queuedSyncCount: number;
  photoCount: number;
  photoBytes: number;
  warningLevel: 'ok' | 'warning' | 'critical';
}

const WARNING_PHOTO_BYTES = 350 * 1024 * 1024;
const CRITICAL_PHOTO_BYTES = 500 * 1024 * 1024;

export async function getStorageHealth(
  database: FieldDatabase,
  input: { locationId?: number | string } = {},
): Promise<StorageHealth> {
  const [sessions, syncQueue] = await Promise.all([database.getAll('capture_sessions'), database.getAll('sync_queue')]);
  const visibleSessions = filterByLocation(sessions, input.locationId);
  const visibleSessionIds = new Set(visibleSessions.map(session => session.id));
  const visiblePhotos = visibleSessions
    .filter(session => session.status !== CAPTURE_SESSION_STATUSES.synced)
    .flatMap(session => session.photos);
  const draftSessions = visibleSessions.filter(
    session => session.status === CAPTURE_SESSION_STATUSES.draft || session.status === CAPTURE_SESSION_STATUSES.ready,
  );
  const visibleSyncQueue = filterBySessionId(syncQueue, visibleSessionIds, input.locationId);
  // ponytail: Exact thumbnail bytes would require a metadata-only store; session metadata avoids loading every Blob.
  const photoBytes = visiblePhotos.reduce((total, photo) => total + photo.sizeBytes, 0);

  return {
    draftCount: draftSessions.length,
    queuedSyncCount: visibleSyncQueue.filter(
      record => record.status === SYNC_QUEUE_STATUSES.queued || record.status === SYNC_QUEUE_STATUSES.syncFailed,
    ).length,
    photoCount: visiblePhotos.length,
    photoBytes,
    warningLevel: getWarningLevel(photoBytes),
  };
}

function filterByLocation<T extends { locationId?: number | string }>(
  records: T[],
  locationId: number | string | undefined,
) {
  if (typeof locationId === 'undefined') {
    return records;
  }

  return records.filter(record => String(record.locationId ?? '') === String(locationId));
}

function filterBySessionId<T extends { sessionId: string }>(
  records: T[],
  sessionIds: Set<string>,
  locationId: number | string | undefined,
) {
  if (typeof locationId === 'undefined') {
    return records;
  }

  return records.filter(record => sessionIds.has(record.sessionId));
}

function getWarningLevel(photoBytes: number): StorageHealth['warningLevel'] {
  if (photoBytes >= CRITICAL_PHOTO_BYTES) {
    return 'critical';
  }

  if (photoBytes >= WARNING_PHOTO_BYTES) {
    return 'warning';
  }

  return 'ok';
}
